import api from "../../../utils/api";

// ---------------------------------------------------------------------------
// Cliente API de la Bitácora diaria (#528): el feed de resúmenes por persona y
// día, y la generación a demanda (solo administrador). Las respuestas vienen
// como `{ meta, data }` (`response` del backend): se devuelve `data`.
//
// Las rutas se declaran aquí, junto al resto del contrato del feed, para que la
// sección no dependa de que el paquete compartido las publique.
// ---------------------------------------------------------------------------

export const WORK_HOURS_FEED_ENDPOINTS = {
  dailyNotes: "/workHoursDailyNotes",
  dailyNotesGenerate: "/workHoursDailyNotesGenerate",
} as const;

/**
 * Tiempo de espera de la generación a demanda: 5 minutos.
 *
 * El cliente trae 30 s por defecto, y redactar un día es una llamada al modelo
 * POR PERSONA con horas: un día de equipo se pasa de 30 s sin despeinarse. Lo
 * que ocurría es que axios cortaba la petición mientras el servidor seguía
 * trabajando —y terminaba bien—, así que la pantalla daba por fallido un
 * trabajo ya hecho. El tope se alinea con lo que tarda de verdad la corrida.
 */
export const GENERATE_TIMEOUT_MS = 300_000;

const data = <T,>(res: { data?: { data?: unknown } }): T => res?.data?.data as T;

/** `ai_generated`: Claude lo redactó. `fallback`: se armó solo con los datos. */
export type DailyNoteStatus = "ai_generated" | "fallback";

export interface DailyNote {
  id: string;
  company_id: string;
  userId: string;
  userName: string | null;
  date: string;
  text: string;
  wordCount: number;
  status: DailyNoteStatus;
  /** Por qué no hubo redacción: `ai_not_configured`, `ai_failed`, `ai_empty_output`, `ai_invalid_output`. */
  fallbackReason: string | null;
  fallbackDetail: string | null;
  hours: number;
  commitCount: number;
  projects: string[];
  firstHour: number | null;
  lastHour: number | null;
  generatedAt: string;
  model: string | null;
}

export interface DailyNotesResponse {
  from: string;
  to: string;
  scope: "user" | "team";
  userId: string | null;
  notes: DailyNote[];
  /** Solo para administradores: personas por las que se puede filtrar. */
  users?: { userId: string; userName: string }[];
}

export interface GenerateResult {
  date?: string;
  from?: string;
  to?: string;
  people?: number;
  generated: number;
  skipped: number;
  degraded: number;
}

export async function fetchDailyNotes(params: { from?: string; to?: string; userId?: string }): Promise<DailyNotesResponse> {
  return data(await api.get(WORK_HOURS_FEED_ENDPOINTS.dailyNotes, { params }));
}

/**
 * Pide los resúmenes de un día (o de un rango). Sin `force`, los días que ya
 * tienen resumen redactado se saltan: no se vuelve a pagar por ellos.
 *
 * Va con `GENERATE_TIMEOUT_MS` en vez del tope por defecto del cliente: si aquí
 * se agota el tiempo, el servidor puede haber terminado igual, y quien llama
 * tiene que releer el feed antes de concluir nada (ver `describeRequestFailure`).
 */
export async function generateDailyNotes(payload: {
  date?: string;
  from?: string;
  to?: string;
  userId?: string;
  force?: boolean;
}): Promise<GenerateResult> {
  return data(await api.post(WORK_HOURS_FEED_ENDPOINTS.dailyNotesGenerate, payload, { timeout: GENERATE_TIMEOUT_MS }));
}
